import { ReactNode, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface FilterBarProps {
  children: ReactNode;
  expandedContent?: ReactNode;
  label?: string;
  className?: string;
  /**
   * Allow the main row to wrap onto additional lines instead of overflowing
   * when the viewport is too narrow to hold every control. Opt-in so existing
   * single-row bars keep their current behavior.
   */
  wrap?: boolean;
}

function FilterBarRoot({
  children,
  expandedContent,
  label = 'Filters:',
  className = '',
  wrap = false,
}: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const hasExpandableContent = !!expandedContent;

  return (
    <div
      className={`sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 ${className}`}
    >
      {/* Main Row */}
      <div className={`flex items-center gap-3 px-6 py-2 ${wrap ? 'flex-wrap gap-y-2' : ''}`}>
        {label && (
          <span className="text-sm font-medium text-label whitespace-nowrap">
            {label}
          </span>
        )}
        {children}
        {hasExpandableContent && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="ml-auto p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={expanded ? 'Collapse filters' : 'Expand filters'}
          >
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </button>
        )}
      </div>

      {/* Expanded Rows */}
      {hasExpandableContent && expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          {expandedContent}
        </div>
      )}
    </div>
  );
}

function FilterBarRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-6 py-2">
      {children}
    </div>
  );
}

function FilterBarSpacer() {
  return <div className="flex-1" />;
}

function FilterBarDivider() {
  return (
    <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
  );
}

function FilterBarGroup({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="flex items-center gap-2">
      {label && (
        <span className="text-xs font-medium text-muted whitespace-nowrap">
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

export const FilterBar = Object.assign(FilterBarRoot, {
  Row: FilterBarRow,
  Spacer: FilterBarSpacer,
  Divider: FilterBarDivider,
  Group: FilterBarGroup,
});

export default FilterBar;
